import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LayoutDashboardIcon, LogOutIcon, MenuIcon, SearchIcon, UserIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { primaryNavigation } from '../data/navigation';
import { pagePadding } from '../utils/styles';
interface HeaderProps {
  transparentAtTop: boolean;
}
const iconButton = 'inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';
export function Header({
  transparentAtTop
}: HeaderProps) {
  const {
    session,
    logout
  } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  const solid = !transparentAtTop || scrolled || menuOpen;
  return <header className={`fixed inset-x-0 top-0 z-40 border-b transition-[background-color,border-color] duration-200 ease-out ${solid ? 'border-graphite bg-night/95 backdrop-blur-md' : 'border-transparent bg-transparent'}`} data-id="element-155" data-mp="2hpep8g32175o">
      {!solid && <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-night/80 to-transparent" data-id="element-156" data-mp="35khg0tlf5kos" />}
      <div className={`relative flex h-16 items-center gap-10 md:h-[72px] ${pagePadding}`} data-id="element-157" data-mp="1wzaccnd9ff2">
        <Link to="/" aria-label="NICOLE, ir al inicio" className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-night" data-id="element-158" data-mp="3tbddo7ck3a3y">
          <Logo className="text-[17px] md:text-xl" data-id="element-159" data-mp="1wbuy4pgyyek2" />
        </Link>

        <nav aria-label="Navegación principal" className="hidden lg:block" data-id="element-160" data-mp="1hojk8gxbeigs">
          <ul className="flex items-center gap-7" data-id="element-161" data-mp="1mml77cxv31xj">
            {primaryNavigation.map(item => <li key={item.to} data-id="element-162" data-mp="18nbskhpb0fvo">
                <NavLink to={item.to} end={item.to === '/'} className={({
              isActive
            }) => `relative block rounded-sm py-2 text-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${isActive ? 'font-semibold text-white' : 'text-mist hover:text-white'}`} data-id="element-163" data-mp="18mafk7oq9jbb">
                  {({
                isActive
              }) => <>
                      {item.label}
                      {isActive && <span aria-hidden="true" className="absolute -bottom-px left-0 h-0.5 w-full rounded-full bg-action" data-id="element-164" data-mp="mf680ijg2o1s" />}
                    </>}
                </NavLink>
              </li>)}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1 md:gap-2" data-id="element-165" data-mp="rcpzwl92ezc7">
          <Link to="/buscar" aria-label="Buscar" className={iconButton} data-id="element-166" data-mp="154w376r4dbu8">
            <SearchIcon className="h-5 w-5" data-id="element-167" data-mp="1578uiairoyjy" />
          </Link>
          {session ? <>
              <Link to="/admin" className="hidden h-10 items-center gap-2 rounded-md px-3 text-sm font-medium text-white transition-colors duration-150 ease-out hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:inline-flex" data-id="element-168" data-mp="108ffuerqv1i7">
                <LayoutDashboardIcon className="h-4 w-4" data-id="element-169" data-mp="3bvvkk313cvy1" />
                Administración
              </Link>
              <button type="button" onClick={logout} aria-label="Cerrar sesión" title="Cerrar sesión" className={iconButton} data-id="element-170" data-mp="22z6uqed6mbgp">
                <LogOutIcon className="h-5 w-5" data-id="element-171" data-mp="27wp2i1fgqkn4" />
              </button>
            </> : <Link to="/acceso" aria-label="Acceso de administración" title="Acceso privado" className={iconButton} data-id="element-172" data-mp="2cy66i5t8un7x">
              <UserIcon className="h-5 w-5" data-id="element-173" data-mp="2gxncd0cl1mdm" />
            </Link>}
          <button type="button" className={`${iconButton} lg:hidden`} aria-expanded={menuOpen} aria-controls="menu-movil" aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} onClick={() => setMenuOpen(v => !v)} data-id="element-174" data-mp="1k98mny9dwid2">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-175" data-mp="1y0don7085mqk" /> : <MenuIcon className="h-5 w-5" data-id="element-176" data-mp="1y1f3lmkd2tdk" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-177" data-mp="11hfwscpm3d9k">
        {menuOpen && <motion.nav id="menu-movil" aria-label="Navegación principal" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="border-t border-graphite bg-night lg:hidden" data-id="element-178" data-mp="15ihvmsfm9nb0">
            <ul className={`flex flex-col py-3 ${pagePadding}`} data-id="element-179" data-mp="jzzgyukg0ewr">
              {primaryNavigation.map(item => <li key={item.to} data-id="element-180" data-mp="f1fv0u5un34e">
                  <NavLink to={item.to} end={item.to === '/'} className={({
              isActive
            }) => `flex items-center gap-3 rounded-md px-2 py-3 font-display text-base font-semibold ${isActive ? 'text-white' : 'text-mist'}`} data-id="element-181" data-mp="f2h6gzqs1rmn">
                    {({
                isActive
              }) => <>
                        <span aria-hidden="true" className={`h-5 w-0.5 rounded-full ${isActive ? 'bg-action' : 'bg-transparent'}`} data-id="element-182" data-mp="3ng69c4xeqpq8" />
                        {item.label}
                      </>}
                  </NavLink>
                </li>)}
              {session && <li data-id="element-183" data-mp="38u0w1vzu9n3o">
                  <Link to="/admin" className="flex items-center gap-3 rounded-md px-2 py-3 font-display text-base font-semibold text-mist sm:hidden" data-id="element-184" data-mp="38thewhi0j2qp">
                    <span aria-hidden="true" className="h-5 w-0.5" data-id="element-185" data-mp="gaeiqon1f3h" />
                    Administración
                  </Link>
                </li>}
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}